import { EmptyState, Skeleton } from '@h2hspark/ui';
import { TicketX } from 'lucide-react';
import * as React from 'react';
import { useParams } from 'react-router-dom';

import { PageBackLink } from '@/member/page-back-link';

import { BACK_TO_EVENT_LABEL, NO_TICKET_DESCRIPTION, NO_TICKET_TITLE } from './constants';
import { TicketCard, useTicketContent } from './ticket-card';

/**
 * The digital ticket at its own address (`MEM-022` AC-1 and AC-2).
 *
 * **The ticket itself is `TicketCard`**, which this page and the dialog the event page opens both
 * render from `useTicketContent` — one ticket, assembled once, however a member reached it. The
 * card's header says where each field comes from and what it refuses to draw.
 *
 * **This route stays, and that is deliberate.** `MEM-047` pins the addresses mailed links point at,
 * and a member who bookmarked their ticket keeps it. Since the 10 September layout the event page's
 * *View your ticket* opens the dialog instead of navigating, which is the prototype's own overlay
 * (`Web App.dc.html` `ticketOpen`); both end at the same card.
 *
 * ## Not built here
 *
 * The rest of `MEM-022` — Tickets & orders, order detail, the receipt (AC-3 to AC-7) — is the
 * account's, and is reached from there.
 */
export function TicketPage(): React.ReactElement {
  const { eventId = '' } = useParams<{ eventId: string }>();
  const content = useTicketContent(eventId);

  if (content.state === 'loading') return <TicketSkeleton />;

  if (content.state === 'no-ticket') {
    return (
      <TicketShell eventId={eventId}>
        <EmptyState
          icon={<TicketX className="h-6 w-6" aria-hidden />}
          title={NO_TICKET_TITLE}
          description={NO_TICKET_DESCRIPTION}
        />
      </TicketShell>
    );
  }

  return (
    <TicketShell eventId={eventId}>
      {/* The overlay's 380px card, centred on the page rather than over one. */}
      <div className="mx-auto w-full max-w-[380px]">
        <TicketCard content={content} className="overflow-hidden rounded-xl bg-background shadow-md" />
      </div>
    </TicketShell>
  );
}

/** The back link and the page's spacing, shared by every state so the way out never moves. */
function TicketShell({
  eventId,
  children,
}: {
  readonly eventId: string;
  readonly children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="space-y-4">
      <PageBackLink to={`/app/events/${encodeURIComponent(eventId)}`}>
        {BACK_TO_EVENT_LABEL}
      </PageBackLink>
      {children}
    </div>
  );
}

function TicketSkeleton(): React.ReactElement {
  return (
    <div className="space-y-4">
      <Skeleton className="h-5 w-32" />
      <Skeleton className="h-96 w-full rounded-xl" />
    </div>
  );
}
